/**
 * Cloze Deletion Utilities
 * Supports syntax: {{answer}} for cloze deletions
 */

/**
 * Check if text contains cloze deletions
 * @param {string} text - The text to check
 * @returns {boolean} - True if text contains {{...}} patterns
 */
export const hasCloze = (text) => {
  if (!text) return false;
  return /\{\{[^}]+\}\}/g.test(text);
};

/**
 * Check if a card is a cloze card
 * @param {object} card - The card object with ar and en properties
 * @returns {boolean} - True if either side has cloze deletions
 */
export const isClozeCard = (card) => {
  return hasCloze(card.ar) || hasCloze(card.en);
};

/**
 * Parse text and extract cloze parts
 * @param {string} text - The text with {{...}} patterns
 * @returns {array} - Array of {type, content} objects
 */
export const parseCloze = (text) => {
  if (!text) return [];

  const parts = [];
  const regex = /\{\{([^}]+)\}\}/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the cloze
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }

    // Add the cloze deletion
    parts.push({
      type: 'cloze',
      content: match[1]
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }

  return parts;
};

/**
 * Render cloze text with blanks
 * @param {string} text - The text with {{...}} patterns
 * @param {boolean} revealed - Whether to show answers
 * @returns {array} - Array of parts for rendering
 */
export const renderCloze = (text, revealed = false) => {
  const parts = parseCloze(text);

  return parts.map((part, index) => ({
    ...part,
    id: index,
    revealed: revealed && part.type === 'cloze'
  }));
};

/**
 * Get plain text without cloze markup
 * @param {string} text - The text with {{...}} patterns
 * @returns {string} - Plain text with cloze answers visible
 */
export const getClozeText = (text) => {
  if (!text) return '';
  return text.replace(/\{\{([^}]+)\}\}/g, '$1');
};

/**
 * Count number of cloze deletions in text
 * @param {string} text - The text to check
 * @returns {number} - Number of cloze deletions
 */
export const countCloze = (text) => {
  if (!text) return 0;
  const matches = text.match(/\{\{[^}]+\}\}/g);
  return matches ? matches.length : 0;
};
